import React, { useState, useEffect } from "react";
import { Button, Form, InputGroup, Row, Col, Table, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { SurveyApiConfig } from "../../../../config/surveyApi";
import axios from "axios";
import { getApiConfig } from "../../../../helpers/surveyUtils";
import { ShowToastError } from "../../ShowToastError";

const PAGE_SIZE = 10;

export default function GroupSelection({
  app_module,
  remaining_groups,
  allowed_groups,
  survey_name,
  closeModal,
  updateGroups,
  setCount,
}) {
  const [combinedGroups, setCombinedGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroups, setSelectedGroups] = useState(allowed_groups || []);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [existingTemplates, setExistingTemplates] = useState({});
  const [pageIndex, setPageIndex] = useState(0);

  // Fetch existing templates on component mount
  useEffect(() => {
    const fetchFeatureData = async () => {
      const config = getApiConfig();
      try {
        const response = await axios.get(SurveyApiConfig.xSurveyDetails, config);
        setExistingTemplates(response?.data?.settings || {});
      } catch (error) {
        ShowToastError({ error });
      }
    };

    fetchFeatureData();
  }, []);

  // Combine and sort groups so that allowed_groups come first
  useEffect(() => {
    const combined = [...remaining_groups, ...allowed_groups];
    const sortedCombined = combined.sort((a, b) => {
      const isAAllowed = allowed_groups.some((group) => group.id === a.id);
      const isBAllowed = allowed_groups.some((group) => group.id === b.id);

      if (isAAllowed && !isBAllowed) return -1;
      if (!isAAllowed && isBAllowed) return 1;
      return 0;
    });

    setCombinedGroups(sortedCombined);
    setFilteredGroups(sortedCombined);
  }, [remaining_groups, allowed_groups]);

  // Filter groups based on search term
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredGroups(combinedGroups);
    } else {
      setFilteredGroups(
        combinedGroups.filter((group) =>
          group.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    setPageIndex(0);
  }, [searchTerm, combinedGroups]);

  // Handle checkbox change (assign/unassign)
  const handleCheckChange = (is_checked, selectedGroup) => {
    setSelectedGroups((prevSelectedGroups) => {
      if (is_checked) {
        return [...prevSelectedGroups, selectedGroup];
      } else {
        return prevSelectedGroups.filter(
          (group) => group.id !== selectedGroup.id
        );
      }
    });
  };

  // Handle updating groups via API
  const handleUpdateGroups = async () => {
    const newTemplateKey = survey_name?.key.toLowerCase().replace(/ /g, "_");

    // Create the updated template with only the currently selected groups
    const updatedTemplate = {
      ...(existingTemplates[newTemplateKey] || {}),
      selected_groups: selectedGroups.map((group) => ({
        name: group.name,
        description: group.description,
        ticket_count: group.ticket_count,
        id: group.id,
      })),
    };

    // Merge the updated template with existing templates
    const updatedTemplates = {
      ...existingTemplates,
      [newTemplateKey]: updatedTemplate,
    };

    try {
      setIsLoadingGroups(true);
      const config = getApiConfig();

      // Send the updated templates to the API
      await axios.post(
        SurveyApiConfig.xSurveyUpdateSettings,
        { settings: updatedTemplates },
        config
      );

      // Show success message
      toast.success("Groups updated successfully in the template");

      // Update the state of remainingGroups and ingestedGroups
      const updatedRemainingGroups = remaining_groups.filter(
        (group) => !selectedGroups.some((selected) => selected.id === group.id)
      );
      const updatedIngestedGroups = selectedGroups;

      // Pass the updated groups back to the parent component
      updateGroups(updatedRemainingGroups, updatedIngestedGroups);

      // Reset the selectedGroups state to empty
      setSelectedGroups([]);
    } catch (error) {
      ShowToastError({ error });
    } finally {
      setIsLoadingGroups(false);
      closeModal(false);
      setCount((prevCount) => prevCount + 1);
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredGroups.length / PAGE_SIZE);
  const currentPageData = filteredGroups.slice(
    pageIndex * PAGE_SIZE,
    (pageIndex + 1) * PAGE_SIZE
  );

  return (
    <div className="my-2">
      <h6 className="mb-2">Groups</h6>
      <Row className="mb-3">
        <Col xs={12} sm={6} md={6} lg={6}>
          <InputGroup size="sm">
            <Form.Control
              placeholder="Search groups..."
              aria-label="Search Groups"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
          </InputGroup>
        </Col>
      </Row>

      <Table bordered striped size="sm" className="fs--1 mb-0">
        <thead className="bg-200 text-900 text-nowrap align-middle">
          <tr>
            <th>Group Name</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {currentPageData.length > 0 ? (
            currentPageData.map((group) => (
              <tr key={group.id} className="align-middle">
                <td>
                  <Form.Check
                    type="checkbox"
                    name="groupSelection"
                    id={`checkbox-${group.name}`}
                    className="my-auto"
                    label={<span className="ms-2">{group.name}</span>}
                    checked={selectedGroups.some((g) => g.id === group.id)}
                    onChange={(e) => handleCheckChange(e.target.checked, group)}
                  />
                </td>
                <td>
                  <span className="text-wrap">{group.description}</span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={2}>
                <p className="text-muted text-center my-3">No Data Found</p>
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {filteredGroups.length > 0 && (
        <>
          {/* Pagination Controls */}
          <div className="d-flex align-items-center justify-content-between mt-2 px-1">
            <small className="text-muted">
              Showing {pageIndex * PAGE_SIZE + 1} to{" "}
              {Math.min((pageIndex + 1) * PAGE_SIZE, filteredGroups.length)} of{" "}
              {filteredGroups.length} groups
            </small>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={pageIndex === 0}
                onClick={() => setPageIndex((p) => p - 1)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={pageIndex >= totalPages - 1}
                onClick={() => setPageIndex((p) => p + 1)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>

          {/* Update Button */}
          <div
            className="btn-group mx-auto d-flex justify-content-center w-25 mt-3"
            role="group"
          >
            <Button
              variant="success"
              disabled={isLoadingGroups}
              onClick={handleUpdateGroups}
            >
              {isLoadingGroups ? (
                <>
                  <Spinner size="sm" animation="border" className="me-1" />
                  Updating
                </>
              ) : (
                "Update Groups"
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
